// File: frontend/src/components/marketing/partners/PartnersBackdrop.js
//
// The ambient background for /ambassador: three slow glow orbs under a wash,
// fixed behind the page content.
//
// ── IT IS BACKGROUND, AND IT STAYS BACKGROUND ───────────────────────────────
//
// Everything here is transform and opacity on three elements. Specifically NOT
// here, each for a measured reason:
//
//   · No animated blur. filter: blur() over a 600px area is the most expensive
//     thing a marketing page can animate. The orbs are radial gradients, so the
//     softness is painted once and costs nothing per frame.
//   · No particle loop and no scroll-scrubbed parallax. The one network
//     illustration on the page lives in the hero, where it explains something.
//   · No motion while the tab is hidden. A fixed layer never scrolls out of
//     view, so the visibility handler is what actually stops it.
//   · The wash sits ABOVE the orbs and below the content, so body copy never
//     sits directly on a coloured gradient in either theme.
//
// Reduced motion or a low-capability device: the orbs still render, they just
// do not move. Nothing here carries information.
//
// ── STACKING ────────────────────────────────────────────────────────────────
//
// position: fixed; z-index: 0. Page content and MarketingFooter sit at
// position: relative; z-index: 1. z-index: -1 does NOT work here: a negative-z
// fixed child paints behind its parent's opaque background.default.

import React, { useRef } from "react";
import { Box, useTheme, alpha } from "@mui/material";

import { gsap, useGSAP, MQ, prefersLightweightMotion } from "../../../utils/gsapCore";

export default function PartnersBackdrop() {
  const theme = useTheme();
  const root = useRef(null);

  const dark = theme.palette.mode === "dark";
  const brand = theme.palette.primary.main;
  const info = theme.palette.info.main;
  const ground = theme.palette.background.default;

  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add({ reduced: MQ.reduced, always: MQ.always }, (ctx) => {
        if (ctx.conditions.reduced || prefersLightweightMotion()) return undefined;

        // Mismatched durations so the three never fall into a shared rhythm.
        const drifts = [
          { x: 8, y: -6, d: 34 },
          { x: -7, y: 8, d: 41 },
          { x: 6, y: 7, d: 29 },
        ];
        const ambient = gsap.utils.toArray("[data-orb]").map((orb, i) => {
          const s = drifts[i % drifts.length];
          return gsap.to(orb, {
            xPercent: s.x, yPercent: s.y, duration: s.d,
            ease: "sine.inOut", repeat: -1, yoyo: true,
          });
        });

        // Pauses only these tweens, never gsap.globalTimeline, which would also
        // freeze section reveals mid-flight.
        const onVisibility = () => ambient.forEach((t) => (document.hidden ? t.pause() : t.resume()));
        document.addEventListener("visibilitychange", onVisibility);
        return () => document.removeEventListener("visibilitychange", onVisibility);
      });
    },
    { scope: root, dependencies: [theme.palette.mode] }
  );

  return (
    <Box
      ref={root}
      aria-hidden="true"
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 0,
        pointerEvents: "none",
        overflow: "hidden",
        bgcolor: "background.default",
      }}
    >
      <Orb data-orb color={brand} strength={dark ? 0.26 : 0.16}
        sx={{ width: "58vw", height: "58vw", maxWidth: 760, maxHeight: 760, top: "-14%", left: "-10%" }} />
      <Orb data-orb color={info} strength={dark ? 0.18 : 0.11}
        sx={{ width: "50vw", height: "50vw", maxWidth: 660, maxHeight: 660, top: "30%", right: "-16%" }} />
      <Orb data-orb color={brand} strength={dark ? 0.16 : 0.1}
        sx={{ width: "46vw", height: "46vw", maxWidth: 600, maxHeight: 600, bottom: "-20%", left: "20%" }} />

      {/* The wash that keeps copy off the orbs. */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: `linear-gradient(180deg, ${alpha(ground, dark ? 0.5 : 0.55)} 0%, ${alpha(ground, dark ? 0.8 : 0.84)} 50%, ${ground} 100%)`,
        }}
      />
    </Box>
  );
}

/** A radial-gradient orb: the soft falloff of a blur, none of its cost. */
function Orb({ color, strength, sx, ...rest }) {
  return (
    <Box
      {...rest}
      sx={{
        position: "absolute",
        borderRadius: "50%",
        background: `radial-gradient(circle at 50% 50%, ${alpha(color, strength)} 0%, ${alpha(color, strength * 0.45)} 38%, ${alpha(color, 0)} 70%)`,
        ...sx,
      }}
    />
  );
}
